import * as React from "react";
import { observer } from "mobx-react";
import { action } from "mobx";
import {
    ComboBox,
    type IComboBoxOption,
    type IComboBoxProps,
    type ISelectableOption,
} from "@fluentui/react";
import { DateFormat } from "../../api";
import { LoadingState } from "../../utils/loading-state";
import { type UUID } from "../../utils/uuid";

export type FormUpdateDefaultDateFormatProps = Omit<
    IComboBoxProps,
    "options" | "selectedKey" | "onChange"
>;

enum UpdateDefaultDateFormatFeatures {
    CHANGE_DEFAULT_DATE_FORMAT = "change default date format",
}

import { store } from "../../store";

@observer
export class FormUpdateDefaultDateFormat extends React.Component<FormUpdateDefaultDateFormatProps> {
    private readonly loadingFeatures = new LoadingState<UpdateDefaultDateFormatFeatures>();

    private get options(): ISelectableOption<unknown>[] {
        return Object.values(DateFormat).map((format) => ({
            key: format,
            text: store.i18n.t(`page.systemWide.defaultDateFormat.format.${format}`),
        }));
    }

    @action.bound private async updateDefaultDateFormat(
        publicSettingId: UUID,
        options: IComboBoxOption | undefined,
    ): Promise<void> {
        const defaultDateFormat = options?.key as DateFormat;
        await this.loadingFeatures.wrap(
            UpdateDefaultDateFormatFeatures.CHANGE_DEFAULT_DATE_FORMAT,
            store.repositoryPublicSettings.update(publicSettingId, { defaultDateFormat }),
        );
    }

    public render(): JSX.Element {
        return (
            <ComboBox
                {...this.props}
                disabled={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultDateFormatFeatures.CHANGE_DEFAULT_DATE_FORMAT,
                    ) || this.props.disabled
                }
                label={store.i18n.t("page.systemWide.defaultDateFormat.label")}
                options={this.options}
                selectedKey={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultDateFormatFeatures.CHANGE_DEFAULT_DATE_FORMAT,
                    )
                        ? undefined
                        : store.servicePublicSettings.publicSetting?.defaultDateFormat
                }
                onChange={(_, option) => {
                    if (store.servicePublicSettings.publicSetting) {
                        this.updateDefaultDateFormat(
                            store.servicePublicSettings.publicSetting.id,
                            option,
                        );
                    }
                }}
            />
        );
    }
}
